import { useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import styles from '../../styles/SplitSlideshow.module.css';

export default function SplitSlideshow({
  slides,
  enableGlass = false,
  showDots = true,
  autoPlayInterval = 3000,
}) {
  const sliderLeftRef = useRef(null);
  const sliderRightRef = useRef(null);
  const leftWheelTargetRef = useRef(null);

  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [current, setCurrent] = useState(0);

  const maxItems = slides?.length ?? 0;
  const hasDescription = Array.isArray(slides) && slides.some((s) => Boolean(s?.description));

  useEffect(() => {
    if (!slides?.length) {
      setImagesLoaded(true);
      return;
    }

    const imagePromises = slides.map(
      (slide) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = slide.img;
        })
    );

    Promise.allSettled(imagePromises).then(() => setImagesLoaded(true));
  }, [slides]);

  const settingsLeft = useMemo(
    () => ({
      vertical: true,
      verticalSwiping: true,
      arrows: false,
      infinite: true,
      dots: false,
      speed: 1000,
      autoplay: autoPlayInterval > 0,
      autoplaySpeed: autoPlayInterval,
      pauseOnHover: true,
      cssEase: 'cubic-bezier(0.7, 0, 0.3, 1)',
      beforeChange: (currentSlide, nextSlide) => {
        setCurrent(nextSlide);
        if (maxItems <= 0) return;

        if (currentSlide > nextSlide && nextSlide === 0 && currentSlide === maxItems - 1) {
          sliderRightRef.current?.slickGoTo?.(-1);
        } else if (currentSlide < nextSlide && currentSlide === 0 && nextSlide === maxItems - 1) {
          sliderRightRef.current?.slickGoTo?.(maxItems);
        } else {
          sliderRightRef.current?.slickGoTo?.(maxItems - 1 - nextSlide);
        }
      },
    }),
    [autoPlayInterval, maxItems]
  );

  const settingsRight = useMemo(
    () => ({
      swipe: false,
      vertical: true,
      arrows: false,
      infinite: true,
      speed: 950,
      cssEase: 'cubic-bezier(0.7, 0, 0.3, 1)',
      initialSlide: Math.max(0, maxItems - 1),
    }),
    [maxItems]
  );

  useEffect(() => {
    const target = leftWheelTargetRef.current;
    if (!target) return;

    const handleWheel = (event) => {
      event.preventDefault();
      if (event.deltaX > 0 || event.deltaY < 0) {
        sliderLeftRef.current?.slickNext?.();
      } else if (event.deltaX < 0 || event.deltaY > 0) {
        sliderLeftRef.current?.slickPrev?.();
      }
    };

    target.addEventListener('wheel', handleWheel, { passive: false });
    return () => target.removeEventListener('wheel', handleWheel);
  }, []);

  const reversedSlides = useMemo(() => [...(slides ?? [])].reverse(), [slides]);

  const handleDotClick = (index) => {
    sliderLeftRef.current?.slickGoTo?.(index);
  };

  const activeSlide = slides?.[current];

  if (maxItems === 0) {
    return <div className={styles.splitSlideshow} />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.clip}>
        {!imagesLoaded && (
          <div className={styles.loaderOverlay}>
            <div className={styles.loader} aria-label="Loading slideshow" role="status">
              <div className={`${styles.loaderRing} ${styles.loaderRing1}`} />
              <div className={`${styles.loaderRing} ${styles.loaderRing2}`} />
              <div className={`${styles.loaderRing} ${styles.loaderRing3}`} />
              <div className={`${styles.loaderRing} ${styles.loaderRing4}`} />
            </div>
          </div>
        )}

        <div
          className={styles.splitSlideshow}
          style={{
            opacity: imagesLoaded ? 1 : 0,
            transition: 'opacity 0.5s',
          }}
        >
          <div className={`${styles.slideshow} ${styles.slideshowLeft}`} ref={leftWheelTargetRef}>
            <div className={`${styles.slider} ${styles.sliderLeft}`}>
              <Slider ref={sliderLeftRef} {...settingsLeft}>
                {(slides ?? []).map((slide, index) => (
                  <div key={`left-${index}`} className={styles.item}>
                    <img
                      src={slide.img}
                      alt={slide.title}
                      className={styles.image}
                      draggable={false}
                    />
                  </div>
                ))}
              </Slider>
            </div>
          </div>

          <div className={`${styles.slideshow} ${styles.slideshowRight}`}>
            <div className={`${styles.slider} ${styles.sliderRight}`}>
              <Slider ref={sliderRightRef} {...settingsRight}>
                {reversedSlides.map((slide, index) => (
                  <div key={`right-${index}`} className={styles.item}>
                    <img
                      src={slide.img}
                      alt={slide.title}
                      className={styles.image}
                      draggable={false}
                    />
                  </div>
                ))}
              </Slider>
            </div>
          </div>

          {hasDescription && activeSlide?.description && (
            <div className={styles.descriptionOuter} key={`desc-${current}`}>
              <div className={`${styles.descriptionInner} ${enableGlass ? styles.descriptionInnerGlass : ''}`}>
                <h2 className={styles.title}>{activeSlide.title}</h2>
                <p className={styles.description}>{activeSlide.description}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {showDots && (slides ?? []).length > 0 && (
        <div className={styles.dots}>
          {(slides ?? []).map((_, index) => (
            <button
              key={`dot-${index}`}
              className={`${styles.dot} ${current === index ? styles.dotActive : ''}`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={current === index ? 'true' : 'false'}
              type="button"
            />
          ))}
        </div>
      )}
    </div>
  );
}

